import { Star } from 'lucide-react';

export interface PhotoReview {
  id: string | number;
  rating: number;
  authorName: string;
  text: string;
  imageUrl?: string;
  verified?: boolean;
  createdAt?: string;
}

interface Props {
  reviews: PhotoReview[];
  averageRating?: number;
  totalCount?: number;
  onSeeAll?: () => void;
}

/**
 * Lightweight 2-3 review preview block. Renders nothing if no reviews.
 * Designed to drop above or below the product description.
 */
export function PhotoReviewsPreview({
  reviews,
  averageRating,
  totalCount,
  onSeeAll,
}: Props) {
  if (!reviews || reviews.length === 0) return null;
  const shown = reviews.slice(0, 3);

  return (
    <section aria-label="Customer reviews" className="mt-10">
      <header className="flex items-baseline justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          {typeof averageRating === 'number' && (
            <span className="inline-flex items-center gap-1 text-sm font-semibold">
              <Star className="w-4 h-4 fill-current text-amber-500" aria-hidden="true" />
              {averageRating.toFixed(1)}
            </span>
          )}
          <span className="text-xs text-neutral-500">
            {typeof totalCount === 'number'
              ? `from ${totalCount.toLocaleString()} reviews`
              : `${reviews.length} reviews`}
          </span>
        </div>
        {onSeeAll && (
          <button
            type="button"
            onClick={onSeeAll}
            className="text-sm font-medium underline-offset-4 hover:underline"
          >
            See all
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((r) => (
          <article
            key={r.id}
            className="rounded-2xl border border-neutral-200/60 bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] transition-shadow hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_40px_rgba(0,0,0,0.10)]"
          >
            {r.imageUrl && (
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                <img
                  src={r.imageUrl}
                  alt={`Photo by ${r.authorName}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-4 flex flex-col gap-2">
              <div className="flex items-center gap-1" aria-label={`${r.rating} out of 5 stars`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-current text-amber-500' : 'text-neutral-300'}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <p className="text-sm leading-relaxed text-neutral-800 line-clamp-3">{r.text}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-semibold text-neutral-900">{r.authorName}</span>
                {r.verified && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                    Verified
                  </span>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
